import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Accordion from "../../../../components/accordion/Accordion";
import AlertMessage from "../../../../components/alerts/AlertMessage";
import AddNewItemButton from "../../../../components/buttons/AddNewItemButton";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import SectionItem from "../../../../components/section/section-item/SectionItem";
import {
  updateKnowledgeBaseItems,
  updateKnowledgeItemErrors,
  updateOpenKnowledgeBaseItems,
} from "../../../../redux/actions/KnowledgeBaseActions";
import {
  OPENING_HOURS_KEY,
  TEXT_KNOWLEDGE_ITEM_KEY,
  getKnowledgeItemErrors,
  getKnowledgeItemFieldError,
  isEmptyKnowledgeItem,
  sortKnowledgeBaseItems,
  validateKnowledgeBaseItem,
} from "../../../../services-logic/knowledge-base/knowledge-base-logic";
import { postOpenModal } from "../../../../utils/utilPostMessage";
import KnowledgeBaseAccordionBody from "./KnowledgeBaseAccordionBody";
import KnowledgeBaseAccordionHeader from "./KnowledgeBaseAccordionHeader";

const MENU_OPTIONS = [
  { priority: 1, title: "+ Add opening hours", key: OPENING_HOURS_KEY },
  { priority: 2, title: "+ Add textual information", key: TEXT_KNOWLEDGE_ITEM_KEY },
];

const KnowledgeBaseAccordion = () => {
  const dispatch = useDispatch();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const { productName } = useSelector((state) => state.info.organization);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    config,
    knowledgeItemErrors,
    updatedKnowledgeBaseItems,
    openKnowledgeBaseItems,
    knowledgeBaseAccordionError,
  } = useSelector((state) => state.knowledgeBase);
  const { externalOverlay } = useSelector((state) => state.info);

  const [modalStyles, setModalStyles] = useState("");
  const headerRefs = useRef([]);

  const availableMenuOptions = updatedKnowledgeBaseItems.reduce((acc, item) => {
    return acc.filter((option) => option.key !== item.type || option.key === TEXT_KNOWLEDGE_ITEM_KEY);
  }, MENU_OPTIONS);

  // Initialize headerRefs based on updatedKnowledgeBaseItems length
  useEffect(() => {
    headerRefs.current = updatedKnowledgeBaseItems.map((_, i) => headerRefs.current[i] || React.createRef());
  }, [updatedKnowledgeBaseItems]);

  useEffect(() => {
    const updatedKnowledgeItemErrors = updatedKnowledgeBaseItems.map((knowledgeItem) => {
      return getKnowledgeItemErrors(knowledgeItem);
    });
    dispatch(updateKnowledgeItemErrors(updatedKnowledgeItemErrors));
  }, []);

  const getDeleteModalTitleByType = (type) => {
    switch (type) {
      case OPENING_HOURS_KEY:
        return "Delete opening hours";
      case TEXT_KNOWLEDGE_ITEM_KEY:
        return "Delete knowledge item";
      default:
        return "Delete knowledge item";
    }
  };

  const getDeleteModalBodyByType = (type) => {
    switch (type) {
      case OPENING_HOURS_KEY:
        return "Are you sure you want to delete the opening hours?";
      case TEXT_KNOWLEDGE_ITEM_KEY:
        return "Are you sure you want to delete the knowledge item?";
      default:
        return "Are you sure you want to delete the knowledge item?";
    }
  };

  const showRemoveKnowledgeItemModal = (index) => {
    if (isEmptyKnowledgeItem(updatedKnowledgeBaseItems[index])) {
      deleteKnowledgeItem(index);
      return;
    }

    if (externalOverlay) {
      const message = {
        childrenType: "confirmation",
        id: "delete-knowledge-item",
        index: index,
        headerTitle: getDeleteModalTitleByType(updatedKnowledgeBaseItems[index].type),
        bodyMessage: getDeleteModalBodyByType(updatedKnowledgeBaseItems[index].type),
        cancelText: "Cancel",
        confirmText: "Delete",
      };
      postOpenModal(message);
    } else {
      const headerRef = headerRefs.current[index];
      if (headerRef && headerRef.current && typeof headerRef.current.getBoundingClientRect === "function") {
        const accordionRect = headerRef.current.getBoundingClientRect();

        setModalStyles({
          position: "absolute",
          top: `${accordionRect.top + 20}px `,
          left: "50%",
          transform: "translateX(-50%)",
        });
      } else {
        setModalStyles({});
      }
      setIsConfirmationModalOpen(true);
      setDeletingIndex(index);
    }
  };

  const onOpenKnowledgeBaseItems = (indexs) => {
    dispatch(updateOpenKnowledgeBaseItems(indexs));
  };

  const onAddNewKnowledgeItem = () => {
    if (filteredAvailableMenuOptions.length > 1) {
      setIsMenuOpen(true);
    } else {
      onAddNewItemClicked(availableMenuOptions[0].key);
    }
  };

  const onAddNewItemClicked = (item) => {
    const actions = {
      [OPENING_HOURS_KEY]: onAddOpeningHoursItem,
      [TEXT_KNOWLEDGE_ITEM_KEY]: onAddKnowledgeItem,
    };
    actions[item]?.();
    setIsMenuOpen(false);
  };

  const addItem = (priority, title, type, value) => {
    updatedKnowledgeBaseItems.push({ priority, title, type, value, errors: { title: "", value: "" } });
    const sortedItems = sortKnowledgeBaseItems(updatedKnowledgeBaseItems);
    dispatch(updateKnowledgeBaseItems(sortedItems));
  };

  const onAddOpeningHoursItem = () => {
    addItem(1, "Opening hours", OPENING_HOURS_KEY, []);
    const sortedItems = sortKnowledgeBaseItems(updatedKnowledgeBaseItems);
    const index = sortedItems.findIndex((item) => item.type === OPENING_HOURS_KEY);
    onOpenKnowledgeBaseItems([index]);
  };

  const onAddKnowledgeItem = () => {
    addItem(2, "", TEXT_KNOWLEDGE_ITEM_KEY, "");
    onOpenKnowledgeBaseItems([updatedKnowledgeBaseItems.length - 1]);
  };

  const onUpdateKnowledgeItem = (item, index, key, value) => {
    const updatedItems = [...updatedKnowledgeBaseItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [key]: value,
    };

    dispatch(updateKnowledgeBaseItems(sortKnowledgeBaseItems(updatedItems)));

    const updatedKnowledgeItemErrors = [...knowledgeItemErrors];
    updatedKnowledgeItemErrors[index] = {
      ...updatedKnowledgeItemErrors[index],
      [key]: getKnowledgeItemFieldError(updatedItems[index], key, value),
    };
    dispatch(updateKnowledgeItemErrors(updatedKnowledgeItemErrors));
  };

  useEffect(() => {
    if (externalOverlay) {
      const handleMessage = (event) => {
        const data = event.data;
        if (data.type === "overlay-to-snippet") {
          const extra = data.extra;
          if (extra.id === "delete-knowledge-item" && extra.action === "confirm") {
            deleteKnowledgeItem(extra.index);
          }
        }
      };

      // Add the event listener
      window.addEventListener("message", handleMessage);

      // Clean up the event listener on component unmount or when externalOverlay changes
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [externalOverlay, updatedKnowledgeBaseItems]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".knowledge-menu")) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const deleteKnowledgeItem = (index) => {
    setDeletingIndex(null);
    onOpenKnowledgeBaseItems([]);

    const updatedItems = [...updatedKnowledgeBaseItems];
    updatedItems.splice(index, 1);

    dispatch(updateKnowledgeBaseItems(sortKnowledgeBaseItems(updatedItems)));

    const updatedKnowledgeItemErrors = [...knowledgeItemErrors];
    updatedKnowledgeItemErrors.splice(index, 1);
    dispatch(updateKnowledgeItemErrors(updatedKnowledgeItemErrors));
  };

  const accordionHeader = ({ item, index, isActive }) => {
    return (
      <KnowledgeBaseAccordionHeader
        ref={headerRefs.current[index]}
        item={item}
        index={index}
        isActive={isActive}
        onDelete={(index) => showRemoveKnowledgeItemModal(index)}
      />
    );
  };

  const accordionBody = ({ item, index }) => {
    return (
      <KnowledgeBaseAccordionBody
        item={item}
        index={index}
        onUpdateKnowledgeItem={(key, value) => onUpdateKnowledgeItem(item, index, key, value)}
      />
    );
  };

  const isAllKnowledgeItemsValid = useMemo(
    () => updatedKnowledgeBaseItems?.every((knowledgeItem) => validateKnowledgeBaseItem(knowledgeItem, null).valid),
    [updatedKnowledgeBaseItems]
  );

  const isReachedMaxKnowledgeItems = useMemo(() => {
    return (
      updatedKnowledgeBaseItems.filter((item) => item.type === TEXT_KNOWLEDGE_ITEM_KEY).length >=
      config.maxKnowledgeItemsListSize
    );
  });

  const filteredAvailableMenuOptions = useMemo(() => {
    return availableMenuOptions.filter((option) =>
      isReachedMaxKnowledgeItems ? option.key !== TEXT_KNOWLEDGE_ITEM_KEY : true
    );
  }, [availableMenuOptions, isReachedMaxKnowledgeItems]);

  const knowledgeBaseItemsContent = (
    <div className="flex w-full flex-col gap-4">
      {knowledgeBaseAccordionError && <AlertMessage message={knowledgeBaseAccordionError} />}
      <Accordion
        id="knowledge-base-accordion"
        data={updatedKnowledgeBaseItems || []}
        headerChildren={accordionHeader}
        bodyChildren={accordionBody}
        activeIndexes={openKnowledgeBaseItems}
        setActiveIndexes={onOpenKnowledgeBaseItems}
        singleActive={false}
        itemId="knowledge-item-{index}"
      />
      <div className="relative inline-block">
        <div className="flex flex-col md:flex-row justify-start items-start md:items-center gap-2">
          <AddNewItemButton
            width="w-52"
            title="+ Add knowledge item"
            onClick={onAddNewKnowledgeItem}
            disabled={!isAllKnowledgeItemsValid || filteredAvailableMenuOptions.length === 0}
            onDisabledTooltip={
              !isAllKnowledgeItemsValid ? "Finish filling out incomplete items before adding a new one" : ""
            }
          />
          {updatedKnowledgeBaseItems.filter((item) => item.type === TEXT_KNOWLEDGE_ITEM_KEY).length >=
            config.maxKnowledgeItemsListSize && (
            <span className="text-[10px] md:text-[12px]">
              you've reached the maximum number textual knowledge items.
            </span>
          )}
        </div>

        {isMenuOpen && (
          <div className="z-30 knowledge-menu absolute flex flex-col bottom-full border  bg-white rounded-md shadow-md text-[12px] md:text-[14px]">
            {filteredAvailableMenuOptions.map((item, index) => {
              return (
                <button
                  key={`menu-option-${index}`}
                  className="w-full px-4 py-2 text-start text-ov-green font-semibold border-b rounded-md hover:bg-ov-green hover:text-white disabled:opacity-50 disabled:pointer-events-none disabled:text-slate-700"
                  onClick={() => onAddNewItemClicked(item.key)}
                >
                  {item.title}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {!externalOverlay && isConfirmationModalOpen && (
        <ConfirmationModal
          onClose={() => setIsConfirmationModalOpen(false)}
          dataFromSnippet={{
            headerTitle: getDeleteModalTitleByType(updatedKnowledgeBaseItems[deletingIndex].type),
            bodyMessage: getDeleteModalBodyByType(updatedKnowledgeBaseItems[deletingIndex].type),
            cancelText: "Cancel",
            confirmText: "Delete",
          }}
          onConfirm={() => deleteKnowledgeItem(deletingIndex)}
          onCancel={() => setIsConfirmationModalOpen(false)}
          modalStyles={modalStyles}
        />
      )}
    </div>
  );

  return (
    <SectionItem
      title="Knowledge items"
      subtitle={`Knowledge items are the information the ${productName} uses to answer customers queries, including the company address, opening hours, and multiple textual information items.`}
      content={knowledgeBaseItemsContent}
    />
  );
};

export default KnowledgeBaseAccordion;
